// @ts-nocheck
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Box,
  TextField,
  InputAdornment,
  useTheme,
  Grid,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../assets/Icons/lucideIcons/x.svg';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';

import BasicDetails from './BasicDetails/BasicDetails';
import OverrideAttributeModal from './FieldComponents/OverrideAttributeModal';
import useAttribute from '../../../hooks/ModelInventory/useAttribute';
import { ReactComponent as OverrideIcon } from '../../../assets/images/OverrideIcon.svg';
import useEntity from '../../../hooks/ModelInventory/useEntity';
import useModelInventory from '../../../hooks/ModelInventory/useModelInventory';
import { ReactComponent as SchemaIcon } from '../../../assets/Icons/SchemaIcon.svg';
import useSectionPermissions from '../../../hooks/ModelInventory/useSectionPermissions';
import { NOOP } from '../../../const/CommonConst';
import swalToast from '../swalToast';
import FormulaPreviewModal from '../ModelInventoryAndAssociation/Modals/FormulaPreviewModal';

const InfoBox = ({ message, theme }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 200,
      border: `1px solid ${theme?.palette?.other?.calculateBorderColor}`,
      bgcolor: theme?.palette?.other?.calculationBgColor,
      p: 2,
      mb: 2,
    }}
  >
    <Typography
      variant="body1"
      sx={{
        textAlign: 'center',
        fontStyle: 'italic',
      }}
    >
      {message}
    </Typography>
  </Box>
);
InfoBox.propTypes = {
  message: PropTypes.string.isRequired,
  theme: PropTypes.oneOf([PropTypes.object]).isRequired,
};

const DerivedAttributeOverrideModal = ({
  open,
  handleClose,
  attribute,
  entityDetailsFormikForm,
  teamUsersList,
  allAttributeChats,
  entityData,
  isUserAuthorizedToOverrideAttribute,
  formikForm,
  overrideData,
  rowNumber = null,
  advancedTableAttributeId = null,
  refreshSingleAttribute,
  getOverrideData,
  updateInitialValuesFromAPI = NOOP,
  handleAutoSave,
  getAttributeMapping = NOOP,
  allSectionList,
  handleAttributeValueChange = NOOP,
}) => {
  const [formDetails, setFormDetails] = useState([]);
  const [filteredSections, setFilteredSections] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [openFormulaDialog, setOpenFormulaDialog] = useState(false);
  const [selectedAttDerivedFormula, setSelectedAttDerivedFormula] =
    useState(null);
  const [calculatedValue, setCalculatedValue] = useState('');
  const [openOverrideModal, setOpenOverrideModal] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const { calculateAttributeValue, getCalculationRelationAttribute } =
    useAttribute();
  const { updateModelEntity } = useEntity();
  const { getAttributeDetails } = useModelInventory();

  const { handleGetSectionPermission } = useSectionPermissions();
  const theme = useTheme();
  const abortCtrlRef = useRef(null);
  const isInitializedRef = useRef(false);
  // Helper to flatten attributes (for calculation) using either formDetails or filteredSections
  const decidingAttributes = useMemo(() => {
    if (Array.isArray(formDetails) && formDetails.length) return formDetails;
    // otherwise gather attributes from filteredSections (both view & manage)
    return filteredSections?.flatMap((sec) => sec?.attributes || []);
  }, [formDetails, filteredSections]);
  // To filter all derived attributes present in the entity
  const derivedAttributeIds = useMemo(() => {
    if (!Array.isArray(allSectionList)) return [];
    return allSectionList
      .flatMap((section) => section?.attributes || [])
      .filter((attr) => attr?.is_derived === true)
      .map((attr) => String(attr?.attribute_id ?? attr?.id));
  }, [allSectionList]);
  const fetchDecidingAttributes = useCallback(
    async (attrId, entityId, entityType) => {
      // cancel previous request
      if (abortCtrlRef?.current) {
        try {
          abortCtrlRef?.current?.abort();
        } catch {
          // Do nothing
        }
      }
      const controller = new AbortController();
      abortCtrlRef.current = controller;
      setIsFetching(true);
      try {
        const resp = await getCalculationRelationAttribute({
          attribute_id: attrId,
          entity_id: entityId,
          entity_type: entityType,
          signal: controller.signal,
        });
        const data = resp?.data;
        if (!data) {
          setFormDetails([]);
          setFilteredSections([]);
          return;
        }
        if (Array.isArray(data?.attributes)) {
          setFormDetails(data?.attributes);
          setFilteredSections([]);
          return;
        }
        // Sections array
        if (Array.isArray(data)) {
          setFormDetails([]);
          const builtSections = data
            .map((section) => {
              const sectionPermission =
                section?.section_permissions?.[section?.section_name] ??
                'manage';

              // If conditional_show false → hide whole section
              if (!section?.conditional_show) {
                return null;
              }

              // Prepare filtered attributes
              const filteredAttributes = (section?.attributes || [])
                .filter((attr) => attr?.conditional_show !== false)
                .map((attr) => {
                  // Attribute-level permission
                  const attrPermission =
                    attr?.section_permissions?.[section.section_name] ??
                    sectionPermission;

                  return {
                    ...attr,
                    permission: attrPermission,
                    is_editable: attrPermission === 'manage',
                  };
                })
                .filter((attr) => attr?.permission !== 'none'); // hide only these attrs

              return {
                ...section,
                permission: sectionPermission,
                attributes: filteredAttributes,
              };
            })
            .filter((sec) => sec !== null); // keep section even if attributes empty

          setFilteredSections(builtSections);
          return;
        }
        // Fallback
        if (Array.isArray(data?.attributes)) {
          setFormDetails(data?.attributes);
          return;
        }
        setFormDetails([]);
        setFilteredSections([]);
      } catch {
        // Do nothing
      } finally {
        setIsFetching(false);
        abortCtrlRef.current = null;
      }
    },
    [setFormDetails, setFilteredSections]
  );

  useEffect(() => {
    if (!open) return;
    const attrId = attribute?.attribute_id ?? attribute?.id;
    fetchDecidingAttributes(
      attrId,
      entityData?.entity_id,
      entityData?.entity_type ?? 'ModelInventory'
    );
    return () => {
      try {
        abortCtrlRef.current?.abort();
      } catch {
        // Do nothing
      }
      abortCtrlRef.current = null;
    };
  }, [open, attribute]);

  const handleCalculate = useCallback(async () => {
    try {
      const allAttributes = [...decidingAttributes];
      setIsCalculating(true);
      const input_values = {};
      decidingAttributes.forEach((attr) => {
        const id = attr?.attribute_id ?? attr?.id;
        input_values[id] =
          formValues[id] ?? // user-modified value
          entityDetailsFormikForm?.values[id] ?? // Formik current value
          attr?.default_value ??
          null;
      });
      allAttributes?.forEach((attr) => {
        const id = attr?.attribute_id ?? attr?.id;

        input_values[id] =
          formValues[id] ??
          entityDetailsFormikForm?.values[id] ??
          attr?.default_value ??
          null;
      });

      const payload = {
        entity_id: entityData?.entity_id,
        entity_type: entityData?.entity_type,
        attribute_id: attribute?.attribute_id ?? attribute?.id,
        input_values,
      };
      const res = await calculateAttributeValue(payload);
      const calculated = res?.data?.attribute_value ?? '';
      setCalculatedValue(calculated);
      setIsCalculating(false);
      const message = res?.data?.msg || 'Calculated successfully';
      swalToast({
        icon: res?.data?.status,
        title: message,
      });
    } finally {
      setIsCalculating(false);
    }
  }, [
    decidingAttributes,
    formValues,
    entityDetailsFormikForm,
    entityData,
    attribute,
    calculateAttributeValue,
  ]);
  const formik = useFormik({
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleCalculate(values);
    },
  });

  // Handle change in deciding attributes (from BasicDetails)
  const handleChange = useCallback(
    (key, value) => {
      setFormValues((prev) => ({ ...prev, [key]: value }));
    },
    [setFormValues]
  );
  useEffect(() => {
    if (!open) {
      isInitializedRef.current = false; // reset when modal closes
      return;
    }
    if (!isInitializedRef.current) {
      setFormValues({ ...entityDetailsFormikForm?.initialValues });
      setCalculatedValue('');
      isInitializedRef.current = true;
    }
  }, [open, isInitializedRef]);

  // Open formula dialog for a derived attribute (if BasicDetails or others call this)
  const handleOpenFormulaDialog = useCallback(() => {
    setSelectedAttDerivedFormula(attribute?.derived_formula);
    setOpenFormulaDialog(true);
  }, [attribute]);

  const handleCloseDerivedModal = useCallback(() => {
    setOpenFormulaDialog(false);
  }, [setOpenFormulaDialog]);

  const handleReset = useCallback(() => {
    setCalculatedValue('');
    const initial = entityDetailsFormikForm?.initialValues || {};
    // Restore LOCAL state values
    setFormValues(initial);
    // Restore FORMik values for deciding attributes
    decidingAttributes?.forEach((attr) => {
      const id = attr?.attribute_id ?? attr?.id;
      if (attr?.is_derived) {
        return;
      }
      entityDetailsFormikForm?.setFieldValue(id, initial[id]);
    });
  }, [decidingAttributes, entityDetailsFormikForm]);

  const handleOpenOverrideModal = useCallback(() => {
    setOpenOverrideModal(true);
  }, [setOpenOverrideModal]);

  const fetchStableEntity = useCallback(
    async (maxRetries = 5) => {
      let prevHash = null;
      // Keep fetching entity details until backend-calculated attributes
      // become STABLE. Backend may take time to update derived attributes.
      // We detect stability by comparing the hash of the current response
      // with the hash of the previous response. When they match, data is stable.
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        const res = await getAttributeDetails({
          entity_type: entityData?.entity_type,
          entity_id: entityData?.entity_id,
        });

        const data = res?.data ?? {};

        // Fast content hash using JSON.stringify
        const hash = JSON.stringify(data);
        // If hash matches previous iteration -> no changes -> stable snapshot found
        if (hash === prevHash) {
          return data;
        }
        // Save hash for next iteration comparison
        prevHash = hash;
        // Wait briefly before next retry (backend recalculations may still be happening)
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      // Fallback:
      const finalRes = await getAttributeDetails({
        entity_type: entityData?.entity_type,
        entity_id: entityData?.entity_id,
      });

      return finalRes?.data;
    },
    [entityData?.entity_id, entityData?.entity_type, getAttributeDetails]
  );
  // Always get a flat list of deciding attributes (derived + non-derived)
  const allDecidingAttributes = useMemo(() => {
    if (Array.isArray(formDetails) && formDetails.length > 0) {
      return formDetails;
    }
    if (Array.isArray(filteredSections) && filteredSections.length > 0) {
      return filteredSections.flatMap((sec) =>
        (sec.attributes || []).map((attr) => ({
          ...attr,
          sectionName: sec.section_name,
          permission: attr.permission,
        }))
      );
    }

    return [];
  }, [formDetails, filteredSections]);

  const handleSave = useCallback(async () => {
    try {
      const modalAttrIds = allDecidingAttributes.map((a) =>
        String(a.attribute_id ?? a.id)
      );
      // Detect only updated attributes
      const manuallyUpdated = {};
      modalAttrIds.forEach((id) => {
        const oldVal = entityDetailsFormikForm.initialValues[id];
        const newVal = formValues[id];

        if (!isEqual(newVal, oldVal)) {
          manuallyUpdated[id] = newVal;
        }
      });
      const input_values = {};
      modalAttrIds.forEach((id) => {
        input_values[id] =
          formValues[id] ?? entityDetailsFormikForm.values[id] ?? null;
      });
      const payload = {
        ...input_values,
        entity_id: entityData.entity_id,
        entity_type: entityData.entity_type,
        reset_overrides: [attribute.id],
      };
      await updateModelEntity(payload);
      await new Promise((res) => setTimeout(res, 150));
      // Fetch stable recalculated values
      const stableData = await fetchStableEntity();
      // Extract only derived attributes values
      const derivedUpdated = {};
      modalAttrIds.forEach((id) => {
        const attr = allDecidingAttributes?.find(
          (a) => String(a.attribute_id ?? a.id) === id
        );
        if (attr?.is_derived && stableData[id] !== undefined) {
          derivedUpdated[id] = stableData[id];
        }
      });
      const outputId = String(attribute?.attribute_id ?? attribute?.id);
      const outputUpdated = { [outputId]: calculatedValue };
      const filteredStableData = {};
      derivedAttributeIds.forEach((id) => {
        const stableVal = stableData[id];
        if (stableVal !== undefined) {
          filteredStableData[id] = stableVal;
        }
      });
      // Merge derived attributes with manually updated values and calculated
      const finalFilteredData = {
        ...entityDetailsFormikForm.values,
        ...filteredStableData,
        ...manuallyUpdated,
        ...outputUpdated,
      };
      Object.entries(finalFilteredData).forEach(([id, value]) =>
        handleAutoSave(id, value)
      );
      if (entityDetailsFormikForm.values) {
        updateInitialValuesFromAPI(finalFilteredData);
      }
      for (const [id, val] of Object.entries(finalFilteredData)) {
        await refreshSingleAttribute(id, val);
      }
      setFormValues(finalFilteredData);

      // Notify the constraint system about the updated derived attribute value
      // so that any conditional dropdowns depending on this derived attribute
      // re-evaluate their options immediately without requiring a save/reload.
      const outputVal = finalFilteredData[outputId];
      if (outputId && handleAttributeValueChange !== NOOP) {
        const prevOutputVal =
          entityDetailsFormikForm?.initialValues?.[outputId];
        handleAttributeValueChange(outputId, outputVal, prevOutputVal);
      }

      await getOverrideData();
      await getAttributeMapping();
      handleClose();
    } catch (err) {
      console.error(err);
    }
  }, [
    allDecidingAttributes,
    formValues,
    calculatedValue,
    entityData,
    attribute,
    entityDetailsFormikForm,
    updateInitialValuesFromAPI,
    handleAutoSave,
    refreshSingleAttribute,
    handleClose,
    updateModelEntity,
    fetchStableEntity,
    getOverrideData,
    getAttributeMapping,
    derivedAttributeIds,
    handleAttributeValueChange,
  ]);

  const handleOverrideConfirm = useCallback(
    (newVal) => {
      formik?.setFieldValue('outputValue', newVal);
      setCalculatedValue(newVal);
      setOpenOverrideModal(false);
    },
    [formik?.setFieldValue, setCalculatedValue]
  );

  const mergedSectionAttributes = useMemo(() => {
    return filteredSections?.flatMap((sec) => sec?.attributes || []);
  }, [filteredSections]);

  const handleCloseOverride = useCallback(() => {
    setOpenOverrideModal(false);
  }, [setOpenOverrideModal]);

  const permission = useMemo(() => {
    if (filteredSections.some((sec) => sec.permission === 'manage')) {
      return 'manage';
    }
    if (filteredSections.some((sec) => sec.permission === 'view')) {
      return 'view';
    }
    return 'none';
  }, [filteredSections]);

  const isManage = useMemo(() => permission === 'manage', [permission]);
  const isView = useMemo(() => permission === 'view', [permission]);
  const isNone = useMemo(() => permission === 'none', [permission]);

  const isAnyValuePresent = useMemo(() => {
    return decidingAttributes.some((attr) => {
      const id = attr?.attribute_id ?? attr?.id;

      const currentValue =
        formValues[id] ??
        entityDetailsFormikForm?.values[id] ??
        attr?.default_value ??
        null;

      return (
        currentValue !== null &&
        currentValue !== '' &&
        currentValue !== undefined
      );
    });
  }, [decidingAttributes, formValues, entityDetailsFormikForm]);

  const calculateButtonTitle = useMemo(() => {
    if (!isAnyValuePresent) {
      return 'No input values to calculate';
    }
    if (isCalculating) {
      return 'Calculating...';
    }
    return `Calculate ${attribute?.label}`;
  }, [isAnyValuePresent, isCalculating, attribute?.label]);

  const nothingToShowBox = useMemo(() => {
    if (!isFetching && isNone) {
      return (
        <InfoBox
          message="Configured attributes cannot be displayed because the required conditions are not met."
          theme={theme}
        />
      );
    }

    const noData =
      !isFetching &&
      !isNone &&
      (!Array?.isArray(formDetails) || formDetails?.length === 0) &&
      (!Array?.isArray(filteredSections) || filteredSections?.length === 0);

    if (noData) {
      return (
        <InfoBox
          message="No attribute configured for the calculation. Please configure an attribute before proceeding."
          theme={theme}
        />
      );
    }
    return null;
  }, [isNone, isFetching, formDetails, theme]);

  const calculationSection = useMemo(() => {
    if (
      Array.isArray(filteredSections) &&
      filteredSections?.length > 0 &&
      !isNone
    ) {
      return (
        <>
          <Box sx={{ mt: -1, mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Below are the attributes used for
              calculation.
            </Typography>
          </Box>
          {!isNone && mergedSectionAttributes?.length > 0 && (
            <Box
              sx={{
                border: `1px solid ${theme?.palette?.other?.calculateBorderColor}`,
                p: 1,
                mb: 2,
              }}
            >
              <BasicDetails
                attributes={mergedSectionAttributes}
                attributeValues={
                  isView ? (formValues[attribute?.id] ?? '') : formValues
                }
                mode={isManage ? 'edit' : 'view'}
                isMultipleEditMode={isManage}
                isUserAuthorizedToOverrideAttribute={
                  isUserAuthorizedToOverrideAttribute
                }
                hideHeader={true}
                entityDetailsFormikForm={entityDetailsFormikForm}
                teamUsersList={teamUsersList}
                allAttributeChats={allAttributeChats}
                handleAttributeClick={handleOpenFormulaDialog}
                entityData={entityData}
                onAttributeChange={handleChange}
                refreshSingleAttribute={refreshSingleAttribute}
                overrideData={overrideData}
                getOverrideData={getOverrideData}
                getAttributeDetails={getAttributeDetails}
                formValues={formValues}
                setFormValues={setFormValues}
              />

              {/* Calculate button visible only in manage */}
              {isManage && (
                <Box
                  sx={{
                    textAlign: 'right',
                    mr: 4,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCalculate}
                    disabled={!isAnyValuePresent || isCalculating || isFetching}
                    title={calculateButtonTitle}
                  >
                    {isCalculating
                      ? `Calculating...`
                      : `Calculate ${attribute?.label || ''}`}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </>
      );
    }

    return null;
  }, [
    filteredSections,
    isNone,
    mergedSectionAttributes,
    theme,
    isView,
    entityDetailsFormikForm,
    formValues,
    isManage,
    isUserAuthorizedToOverrideAttribute,
    teamUsersList,
    allAttributeChats,
    handleOpenFormulaDialog,
    entityData,
    handleChange,
    refreshSingleAttribute,
    overrideData,
    isAnyValuePresent,
    isCalculating,
    isFetching,
    calculateButtonTitle,
    attribute,
    handleCalculate,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { maxHeight: '90vh', overflowY: 'auto' },
          },
        }}
        aria-labelledby="derived-attr-override-title"
      >
        <DialogTitle
          id="derived-attr-override-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1">
            Calculation Deciding Attributes - {attribute?.label}
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label="close modal"
            title="Close"
            color="primary"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {calculationSection}

          {/* If nothing to show */}
          {nothingToShowBox}

          {/* VIEW DERIVED FORMULA LINK */}
          <Grid size={12}>
            <Button
              sx={{
                padding: '4px 16px 4px 0px',
                mb: 1,
              }}
              startIcon={<SchemaIcon />}
              onClick={handleOpenFormulaDialog}
              title="View Derived Formula"
            >
              View Derived Formula
            </Button>
          </Grid>
          {/* OUTPUT FIELD */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1">Output Attribute</Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              label={attribute?.label}
              placeholder="Calculated value..."
              value={calculatedValue}
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      {isUserAuthorizedToOverrideAttribute && (
                        <IconButton
                          onClick={handleOpenOverrideModal}
                          title={`Override ${attribute?.label}`}
                          edge="end"
                        >
                          <OverrideIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                },

                inputLabel: {
                  shrink: true,
                  sx: {
                    fontSize: '14px',
                    mb: 1,
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ gap: '8px' }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!isAnyValuePresent}
            title={
              !isAnyValuePresent
                ? 'No values to reset'
                : 'Reset all entered values'
            }
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isAnyValuePresent || isCalculating || isFetching}
            title={!isAnyValuePresent ? 'No values to Save' : 'Save'}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {openFormulaDialog && (
        <FormulaPreviewModal
          open={openFormulaDialog}
          onClose={handleCloseDerivedModal}
          title="View Derived Formula"
          previewValue={selectedAttDerivedFormula}
        />
      )}
      {openOverrideModal && (
        <OverrideAttributeModal
          open={openOverrideModal}
          onClose={handleCloseOverride}
          onConfirm={handleOverrideConfirm}
          attribute={attribute}
          handleClose={handleClose}
          isLocalAttributeOn={true}
          entityData={entityData}
          overrideData={overrideData}
          rowNumber={rowNumber}
          advancedTableAttributeId={advancedTableAttributeId}
          formikForm={formikForm}
          refreshSingleAttribute={refreshSingleAttribute}
          getOverrideData={getOverrideData}
          handleGetSectionPermission={handleGetSectionPermission}
          getAttributeMapping={getAttributeMapping}
          handleAttributeValueChange={handleAttributeValueChange}
        />
      )}
    </>
  );
};

DerivedAttributeOverrideModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  attribute: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      label: PropTypes.string,
      is_editable: PropTypes.bool,
      derived_formula: PropTypes.string,
    })
  ).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    values: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
        PropTypes.object,
      ])
    ),
    initialValues: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
        PropTypes.object,
      ])
    ),
    setFieldValue: PropTypes.func,
  }),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  allAttributeChats: PropTypes.arrayOf(PropTypes.shape({})),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }),
  isUserAuthorizedToOverrideAttribute: PropTypes.bool.isRequired,
  overrideData: PropTypes.oneOf([PropTypes.object]).isRequired,
  rowNumber: PropTypes.number,
  advancedTableAttributeId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  formikForm: PropTypes.oneOf([PropTypes.object]).isRequired,
  refreshSingleAttribute: PropTypes.func.isRequired,
  getOverrideData: PropTypes.func.isRequired,
  handleAutoSave: PropTypes.func.isRequired,
  updateInitialValuesFromAPI: PropTypes.func.isRequired,
  getAttributeMapping: PropTypes.func,
  allSectionList: PropTypes.oneOf([PropTypes.object]).isRequired,
  handleAttributeValueChange: PropTypes.func,
};

export default DerivedAttributeOverrideModal;
